import { AgFilterComponent } from '@ag-grid-community/angular';
import { IFilterParams, IDoesFilterPassParams } from '@ag-grid-community/core';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	ViewEncapsulation
} from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';

import { FilterInstance } from '../../services/datacenter-filter';
import { DatacenterType } from '../../models/datacenter-aggrid-view';
import { Order } from '../../../order/models/order';
import { Batch } from '../../../order/models/batch';
import { Shipment } from '../../../order/models/shipment';
import { Document } from '../../../commercial-documents/models/document';
import { OrderService } from '../../../order/services/order.service';
import { ShipmentsService } from '../../../order/services/shipments.service';
import { OrderByDirection, OrderBy } from '../../utils/constant';
import { BatchesService } from '../../../order/services/batches.service';
import { ScheduleBusinessOrder } from '../../../order/models/schedule-business-order';
import { ScheduleBusinessOrderService } from '../../../order/services/schedule-business-order.service';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import { LoadingSpinnerComponent } from '../../components/utils/loading-spinner.component';
import { DropdownFilter, FilterType } from '../../models/ag-grid';
import { FastLaneItems } from '../../../fastlane/models/fastlane-datacenter-config';
import { FastLaneService } from '../../../fastlane/services/fastlane.service';
import { CountriesService } from '../../../user-management/services/countries.services';
import { Country } from '../../../user-management/models/v2/country';
import { UsersDatacenter } from '../../../users/models/user';
import { Organization } from '../../../organization/models/organization';
import { UserService } from '../../services/user.service';
import { User } from '../../models/user';
import { OrganizationServiceResponse } from '../../../user-management/models/organization-service-info';
import { OrganizationService } from '../../../user-management/services/organization.service';
import { TranslationService } from '../../services/translation.service';

interface DropdownList {
	code: string;
	translation: string;
	fixedList: boolean;
}

interface DatacenterFilterParamsInterface extends IFilterParams {
	filterType?: string;
	orderFields: (keyof Order)[];
	userDatacenterFields: (keyof UsersDatacenter)[];
	userFields: string[];
	organizationFields: (keyof Organization)[];
	batchFields: (keyof Batch)[];
	docFields: (keyof Document)[];
	saOrderFields: (keyof ScheduleBusinessOrder)[];
	fastlaneFields: (keyof FastLaneItems)[];
	removeNoValueFilter?: boolean;
}

// Temporary solution. Hopefully.
// Results were taken from:
// http://sapdbpha.unix.appliarmony.net:8032/com/arcelormittal/
// commercial/ofu/wot/or/OrderAggregates.xsodata/Order?$select=transport_mode,transport_mode_description&$format=json
const staticTransportModeDropdownValues = [
	{
		code: '1',
		translation: 'shipment.tmode.bysea',
		fixedList: true
	},
	{
		code: '2',
		translation: 'shipment.tmode.byrail',
		fixedList: true
	},
	{
		code: '3',
		translation: 'shipment.tmode.byroad',
		fixedList: true
	},
	{
		code: '03',
		translation: 'shipment.tmode.bytrain',
		fixedList: true
	},
	{
		code: '4',
		translation: 'shipment.tmode.byairplane',
		fixedList: true
	},
	{
		code: '5',
		translation: 'shipment.tmode.byinlandwaterways',
		fixedList: true
	},
	{
		code: '6',
		translation: 'shipment.tmode.byrail.feve',
		fixedList: true
	},
	{
		code: '7',
		translation: 'shipment.tmode.byrailroad',
		fixedList: true
	},
	{
		code: 'U',
		translation: 'shipment.tmode.notspecified',
		fixedList: true
	}
];

const staticTransportModeDocumentDropdownValues = [
	{
		code: 'CAFER',
		translation: 'tmode.lorryrailway',
		fixedList: true
	},
	{
		code: 'CAM',
		translation: 'tmode.lorry',
		fixedList: true
	},
	{
		code: 'FER',
		translation: 'tmode.rail',
		fixedList: true
	},
	{
		code: 'FLUV',
		translation: 'tmode.inlandwaterway',
		fixedList: true
	},
	{
		code: 'MARIT',
		translation: 'tmode.bysea',
		fixedList: true
	},
	{
		code: 'OTHER',
		translation: 'tmode.othertospecify',
		fixedList: true
	},
	{
		code: 'PLA',
		translation: 'tmode.byair',
		fixedList: true
	}
];

const staticDocumentTypeDropdownValues = [
	{
		code: 'IN',
		translation: 'commercial-documents.invoice',
		fixedList: true
	},
	{
		code: 'OA',
		translation: 'order.card.document.order.acknowledgments',
		fixedList: true
	},
	{
		code: 'DA',
		translation: 'datacenter-filter.dispatch-advice',
		fixedList: true
	},
	{
		code: 'CE',
		translation: 'order.card.document.certificates',
		fixedList: true
	},
	{
		code: 'XCARB',
		translation: 'order.card.xcarb',
		fixedList: true
	}
];

const staticPrevisionItemDropdownValues = [
	{
		code: 'novalue',
		translation: 'global.no',
		fixedList: true
	},
	{
		code: 'X',
		translation: 'global.yes',
		fixedList: true
	}
];

const staticProformaDropdownValues = [
	{
		code: 'true',
		translation: 'global.yes',
		fixedList: true
	},
	{
		code: 'false',
		translation: 'global.no',
		fixedList: true
	}
];

const staticOrderTypeDropdownValues = [
	{
		code: 'spot',
		translation: 'filter.spot-order',
		fixedList: true
	},
	{
		code: 'scheduled',
		translation: 'filter.schedule-business-order',
		fixedList: true
	}
];

const staticDocumentSubtypeDropdownValues = [
	{
		code: '01',
		translation: 'doc-ce-subtype.01',
		fixedList: true
	},
	{
		code: '02',
		translation: 'doc-ce-subtype.02',
		fixedList: true
	},
	{
		code: '17',
		translation: 'doc-ce-subtype.17',
		fixedList: true
	},
	{
		code: '51',
		translation: 'doc-ce-subtype.51',
		fixedList: true
	},
	{
		code: '52',
		translation: 'doc-ce-subtype.52',
		fixedList: true
	},
	{
		code: 'CAR',
		translation: 'doc-ce-subtype.car',
		fixedList: true
	},
	{
		code: 'CCONF',
		translation: 'doc-ce-subtype.cconf',
		fixedList: true
	},
	{
		code: 'CCPU',
		translation: 'doc-ce-subtype.ccpu',
		fixedList: true
	},
	{
		code: 'CEM',
		translation: 'doc-ce-subtype.cem',
		fixedList: true
	},
	{
		code: 'CERAN',
		translation: 'doc-ce-subtype.ceran',
		fixedList: true
	},
	{
		code: 'CERRE',
		translation: 'doc-ce-subtype.cerre',
		fixedList: true
	},
	{
		code: 'CERUS',
		translation: 'doc-ce-subtype.cerus',
		fixedList: true
	},
	{
		code: 'CINSP',
		translation: 'doc-ce-subtype.cinsp',
		fixedList: true
	},
	{
		code: 'CTL',
		translation: 'doc-ce-subtype.ctl',
		fixedList: true
	},
	{
		code: 'FCP',
		translation: 'doc-ce-subtype.fcp',
		fixedList: true
	},
	{
		code: 'OHSAS',
		translation: 'doc-ce-subtype.ohsas',
		fixedList: true
	},
	{
		code: 'AVOIR',
		translation: 'doc-in-subtype.AVOIR',
		fixedList: true
	},
	{
		code: 'DEBIT',
		translation: 'doc-in-subtype.DEBIT',
		fixedList: true
	},
	{
		code: 'FACTU',
		translation: 'doc-in-subtype.FACTU',
		fixedList: true
	},
	{
		code: 'N',
		translation: 'doc-oa-subtype.N',
		fixedList: true
	},
	{
		code: 'O',
		translation: 'doc-oa-subtype.O',
		fixedList: true
	},
	{
		code: 'A',
		translation: 'doc-xcarb-subtype.a',
		fixedList: true
	},
	{
		code: 'B',
		translation: 'doc-xcarb-subtype.b',
		fixedList: true
	},
	{
		code: 'C',
		translation: 'doc-xcarb-subtype.c',
		fixedList: true
	},
	{
		code: 'D',
		translation: 'doc-xcarb-subtype.d',
		fixedList: true
	},
	{
		code: 'E',
		translation: 'doc-xcarb-subtype.e',
		fixedList: true
	},
	{
		code: 'F',
		translation: 'doc-xcarb-subtype.f',
		fixedList: true
	},
	{
		code: 'G',
		translation: 'doc-xcarb-subtype.g',
		fixedList: true
	}
];

const staticSABusinessStatusDropDownValues = [
	{
		code: '01',
		translation: 'saBusinessStatus.start.up',
		fixedList: true

	},
	{
		code: '02',
		translation: 'saBusinessStatus.running',
		fixedList: true
	},
	{
		code: '03',
		translation: 'saBusinessStatus.running.out',
		fixedList: true

	},
	{
		code: '04',
		translation: 'saBusinessStatus.closed',
		fixedList: true

	},
	{
		code: '05',
		translation: 'saBusinessStatus.low.runner',
		fixedList: true

	},
	{
		code: '06',
		translation: 'saBusinessStatus.high.runner',
		fixedList: true
	},
	{
		code: '07',
		translation: 'saBusinessStatus.high.risk.internal',
		fixedList: true
	},
	{
		code: '08',
		translation: 'saBusinessStatus.on.hold',
		fixedList: true
	},
	{
		code: '09',
		translation: 'saBusinessStatus.high.risk.external',
		fixedList: true
	},
];

const staticOrganizationTypeDropdownValues = [
	{
		code: 'Agcy',
		translation: 'organization-type.agency',
		fixedList: true
	},
	{
		code: 'Buyer',
		translation: 'organization-type.buyer',
		fixedList: true
	},
	{
		code: 'LABO',
		translation: 'organization-type.lab',
		fixedList: true
	},
	{
		code: 'SelAg',
		translation: 'organization-type.selag',
		fixedList: true
	},
	{
		code: 'Sell',
		translation: 'organization-type.seller',
		fixedList: true
	},
	{
		code: 'Suser',
		translation: 'organization-type.super-user',
		fixedList: true
	}
];

const staticUserDropdownValues = [
	{
		code: 'DSU',
		translation: 'add-suborg.administrator',
		fixedList: true
	},
	{
		code: 'user',
		translation: 'organizationrole.user',
		fixedList: true
	},
	{
		code: 'hlpdk',
		translation: 'organizationrole.hlpdk',
		fixedList: true
	},
	{
		code: 'subscription',
		translation: 'organizationrole.subscription',
		fixedList: true
	}
];

const staticUserPersonStatusDropdownValues = [
	{
		code: 'Activ',
		translation: 'active',
		fixedList: true
	},
	{
		code: 'dact',
		translation: 'organization.status.deactivated',
		fixedList: true
	},
	{
		code: 'pend',
		translation: 'issue.pending',
		fixedList: true
	}
];

const staticUserOrganizationStatusDropdownValues = [
	{
		code: 'activ',
		translation: 'active',
		fixedList: true
	},
	{
		code: 'pend',
		translation: 'issue.pending',
		fixedList: true
	},
	{
		code: 'userValidationStatus',
		translation: 'wait.user.validation',
		fixedList: true
	},
	{
		code: 'dact',
		translation: 'organization.status.deactivated',
		fixedList: true
	}
];

const staticOrganizationCardUserStatusDropdownValues = staticUserOrganizationStatusDropdownValues;
const staticStockAlertDropdownValues = [
	{
		code: '1',
		translation: 'stock-alert.green',
		fixedList: true
	},
	{
		code: '2',
		translation: 'stock-alert.orange',
		fixedList: true
	},
	{
		code: '3',
		translation: 'stock-alert.red',
		fixedList: true
	}
];

const staticEcallStatusDropdownValues = [
	{
		code: 'O',
		translation: 'ecall-off.open-or-callable',
		fixedList: true
	},
	{
		code: 'C',
		translation: 'ecall-off.closed-or-called',
		fixedList: true
	}
];

const staticClaimTypeDropdownValues = [
	{
		code: 'ZL',
		translation: 'claim.type.zl',
		fixedList: true
	},
	{
		code: 'ZR',
		translation: 'claim.type.zr',
		fixedList: true
	}
];

@Component({
	selector: 'copo-datacenter-dropdown-filter',
	templateUrl: './datacenter-dropdown-filter.component.html',
	styleUrl: './datacenter-dropdown-filter.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,

		MatButtonModule,

		MatFormFieldModule,
		MatIconModule,
		LoadingSpinnerComponent,

		MatSelectModule,
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,
		NgFor,

		MatOptionModule
	]
})
export class DatacenterDropdownFilterComponent implements AgFilterComponent, FilterInstance, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	header: string | undefined;
	sortWay = OrderByDirection.DESC;
	sortField = 'transportMode';
	filters = '';
	filteredItems: DropdownList[] = [];
	isLoading = true;
	datacenterType: DatacenterType = 'order';
	searchTextControl = new FormControl('');
	isClientSide = false;
	datacenterTypeSub?: Subscription;
	itemsSub?: Subscription;
	orders: Order[] = [];
	countries: Country[] = [];
	services: OrganizationServiceResponse[] = [];
	batches: Batch[] = [];
	saOrders: ScheduleBusinessOrder[] = [];
	fastlaneItem: FastLaneItems[] = [];
	orderFields: (keyof Order)[] = [];
	userDatacenterFields: (keyof UsersDatacenter)[] = [];
	userFields: string[] = [];
	organizationFields: (keyof Organization)[] = [];
	batchFields: (keyof Batch)[] = [];
	saOrderFields: (keyof ScheduleBusinessOrder)[] = [];
	fastlaneItemFields: (keyof FastLaneItems)[] = [];
	documents: Document[] = [];
	documentFields: (keyof Document)[] = [];
	index = 0;
	limit = 1500;
	user: User;
	private params?: DatacenterFilterParamsInterface;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly orderService: OrderService,
		private readonly shipmentsService: ShipmentsService,
		private readonly countriesService: CountriesService,
		private readonly batchService: BatchesService,
		private readonly scheduleBusinessOrderService: ScheduleBusinessOrderService,
		private readonly documentService: DocumentService,
		private readonly protoTypeService: FastLaneService,
		private readonly translateService: TranslateService,
		private readonly userService: UserService,
		private readonly organizationService: OrganizationService,
		private readonly translationService: TranslationService,
		private readonly changeDetectorRef: ChangeDetectorRef
	) {
		this.datacenterTypeSub = this.route.data.subscribe(res => {
			this.datacenterType = <DatacenterType> res['type'];
		});
		this.user = this.userService.user;
	}

	async getServices(sortField: string) {
		return firstValueFrom(this.organizationService.getOrganizationServices(
			String(this.user.currentVisibility.organizationId),
			this.userFields,
			this.filters,
			this.index,
			this.limit,
			new OrderBy(<keyof OrganizationServiceResponse>sortField, this.sortWay),
			false
		)).then(services => {
			this.services = services.results;
			this.filteredItems = this.typeConversion().map(x => ({ ...x }));
		}).catch((error: unknown) => {
			console.error('error requesting services', error);
		});
	}

	async agInit(params: DatacenterFilterParamsInterface) {
		this.params = params;
		this.header = this.params.colDef.headerName;
		this.isClientSide = params.rowModel.getType() === 'clientSide';
		this.isLoading = false;
		if (this.datacenterType === 'shipment') {
			if (this.params.colDef.colId === 'transportMode') {
				this.filteredItems = staticTransportModeDropdownValues.map(x => ({ ...x }));
			}
			if (this.params.colDef.colId === 'supplierOrderTypeConcat') {
				this.filteredItems = staticOrderTypeDropdownValues.map(x => ({ ...x }));
			}
			if (this.params.colDef.colId === 'choice' || this.params.colDef.colId === 'endUser'
				|| this.params.colDef.colId === 'form' || this.params.colDef.colId === 'productCategory'
				|| this.params.colDef.colId === 'serviceLevelConcat'
				|| this.params.colDef.colId === 'deliveryModeConcat') {
				this.orderFields = this.params.orderFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getOrders(this.params.colDef.colId);
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
			if (this.params.colDef.colId === 'status' || this.params.colDef.colId === 'carrierNameConcat') {
				this.batchFields = this.params.batchFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getBatches(this.params.colDef.colId);
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
		}

		if (this.datacenterType === 'prototypes'
			&& (this.params.colDef.colId === 'incoterm' || this.params.colDef.colId === 'currency')) {
			this.fastlaneItemFields = this.params.fastlaneFields;
			this.isLoading = true;
			this.changeDetectorRef.markForCheck();
			await this.getFastlaneItem(this.params.colDef.colId);
			this.isLoading = false;
			this.changeDetectorRef.markForCheck();
		}

		if (this.datacenterType === 'order') {
			if (this.params.colDef.colId === 'previsionItem') {
				this.filteredItems = staticPrevisionItemDropdownValues.map(x => ({ ...x }));
			} else if (this.params.colDef.colId === 'stockAlert') {
				this.filteredItems = staticStockAlertDropdownValues.map(x => ({ ...x }));
			} else {
				this.orderFields = this.params.orderFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getOrders(this.params.colDef.colId);
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
		}

		if (this.datacenterType === 'batch'
			|| this.datacenterType === 'calloff-by-batch'
			|| this.datacenterType === 'calloff-by-volume') {
			// eslint-disable-next-line unicorn/prefer-switch
			if (this.params.colDef.colId === 'supplierOrderType') {
				this.filteredItems = staticOrderTypeDropdownValues.map(x => ({ ...x }));
			} else if (this.params.colDef.colId === 'stockAlert') {
				this.filteredItems = staticStockAlertDropdownValues.map(x => ({ ...x }));
			} else if (this.params.colDef.colId === 'ecallStatus') {
				this.filteredItems = staticEcallStatusDropdownValues.map(x => ({ ...x }));
			} else {
				this.batchFields = this.params.batchFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getBatches(this.params.colDef.colId);
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
		}

		if (this.datacenterType === 'users') {
			if (this.params.colDef.colId === 'countryCode') {
				this.userDatacenterFields = this.params.userDatacenterFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getCountries();
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
			if (this.params.colDef.colId === 'role') {
				this.filteredItems = staticUserDropdownValues.map(x => ({ ...x }));
			}
			if (this.params.colDef.colId === 'status') {
				this.filteredItems = staticUserOrganizationStatusDropdownValues.map(x => ({ ...x }));
			}
			if (this.params.colDef.colId === 'personStatus') {
				this.filteredItems = staticUserPersonStatusDropdownValues.map(x => ({ ...x }));
			}
			if (this.params.colDef.colId === 'servicesId') {
				this.userFields = this.params.userFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getServices('id');
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
		}

		if (this.datacenterType === 'organization') {
			if (this.params.colDef.colId === 'address.countryCode') {
				this.organizationFields = this.params.organizationFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getCountries();
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
			if (this.params.colDef.colId === 'role') {
				this.filteredItems = staticOrganizationTypeDropdownValues.map(x => ({ ...x }));
			}
		}

		if (this.datacenterType === 'document' && this.params.colDef.colId) {
			this.documentFields = this.params.docFields;
			switch (this.params.colDef.colId) {
			case 'mill':
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getDocumentMills();
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
				break;
			case 'type':
				this.filteredItems = staticDocumentTypeDropdownValues.map(x => ({ ...x }));

				break;

			case 'subtype':
				this.filteredItems = staticDocumentSubtypeDropdownValues.map(x => ({ ...x }));

				break;

			case 'transportMode':
				this.filteredItems = staticTransportModeDocumentDropdownValues.map(x => ({ ...x }));

				break;
			case 'proforma':
				this.filteredItems = staticProformaDropdownValues.map(x => ({ ...x }));

				break;
			}
		}
		if (this.datacenterType === 'schedule-business-order') {
			if (this.params.colDef.colId === 'businessStatus') {
				this.filteredItems = staticSABusinessStatusDropDownValues.map(x => ({ ...x }));
			} else {
				this.saOrderFields = this.params.saOrderFields;
				this.isLoading = true;
				this.changeDetectorRef.markForCheck();
				await this.getSaOrders(this.params.colDef.colId);
				this.isLoading = false;
				this.changeDetectorRef.markForCheck();
			}
		}

		if (this.datacenterType === 'quality' && this.params.colDef.colId === 'claimType') {
			this.filteredItems = staticClaimTypeDropdownValues.map(x => ({ ...x }));
		}
		if (Array.isArray(this.params.userFields) && this.params.userFields.length && this.filteredItems.length === 0) {
			this.setClientSideUserDropdownValues();
		}
		this.translateAndSortValues();
	}

	isFilterActive() {
		return Boolean(this.searchTextControl.value);
	}

	doesFilterPass(filterParams: IDoesFilterPassParams<Record<string, unknown>>) {
		if (!this.isClientSide) {
			return this.isFilterActive();
		}
		if (!this.isFilterActive()) {
			return true;
		}
		if (!this.params || typeof filterParams.data !== 'object') {
			return false;
		}

		const selected = String(this.searchTextControl.value ?? '').trim().toLowerCase();
		if (this.params.colDef.colId === 'status') {
			return selected === this.getClientSideUserOrganizationStatus(filterParams.data);
		}
		const fields = Array.isArray(this.params.userFields) && this.params.userFields.length
			? this.params.userFields
			: this.params.batchFields;
		const [codeField, translationField] = fields;
		const rawCode = this.getNestedValue(filterParams.data, codeField);
		const rawTranslation = this.getNestedValue(filterParams.data, translationField);
		const codeValue =
			typeof rawCode === 'string' || typeof rawCode === 'number' ? String(rawCode).trim().toLowerCase() : '';
		const translationValue =
			typeof rawTranslation === 'string' || typeof rawTranslation === 'number'
				? String(rawTranslation).trim().toLowerCase()
				: '';

		return selected === codeValue || selected === translationValue;
	}

	getModel(): DropdownFilter | undefined {
		if (this.searchTextControl.value) {
			return {
				customFilterName: this.getCustomFilterName(),
				text: this.searchTextControl.value
			};
		}
		return undefined;
	}

	setModel(code: DropdownFilter | string | null) {
		if (!code) {
			this.searchTextControl.reset();
			return;
		}
		this.searchTextControl.setValue(typeof code === 'string' ? code : code.text);
	}

	getCustomFilterName() {
		if (this.params?.colDef.filterParams && this.params.filterType
			&& (this.params.filterType === 'DOCUMENT_MILL_DROPDOWN_FILTER')) {
			return FilterType.DOCUMENT_MILL_DROPDOWN_FILTER;
		}
		if (this.params?.colDef.filterParams && this.params.filterType
			&& (this.params.filterType === 'SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER')) {
			return FilterType.SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER;
		}
		return FilterType.DROPDOWN_FILTER;
	}

	ngOnDestroy() {
		if (this.datacenterTypeSub) {
			this.datacenterTypeSub.unsubscribe();
		}
	}

	selectionChanged() {
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	resetFilter() {
		this.searchTextControl.reset();
		this.searchTextControl.setValue(null);
		if (this.params) {
			this.params.filterChangedCallback();
		}
	}

	async getOrders(sortField: string | undefined) {
		const isShipmentOrderStyleDropdown = this.datacenterType === 'shipment'
			&& (
				this.params?.colDef.colId === 'productCategory'
				|| this.params?.colDef.colId === 'form'
				|| this.params?.colDef.colId === 'deliveryModeConcat'
				|| this.params?.colDef.colId === 'serviceLevelConcat'
			);
		const ordersFilter = isShipmentOrderStyleDropdown ? undefined : this.filters;
		const effectiveSortField = isShipmentOrderStyleDropdown
			? this.orderFields.length > 0 ? this.orderFields[0] : sortField
			: sortField;
		return firstValueFrom(this.orderService.getDatacenterOrders(
			this.orderFields,
			ordersFilter,
			this.index,
			this.limit,
			new OrderBy(<keyof Order>effectiveSortField, this.sortWay),
			true
		)).then(res => {
			this.orders = res.results;
			this.filteredItems = this.typeConversion().map(x => ({ ...x }));
		}).catch((error: unknown) => {
			console.error('error requesting orders', error);
		});
	}

	async getShipments(sortField: string | undefined) {
		return firstValueFrom(this.shipmentsService.getDatacenterShipments(
			<(keyof Shipment)[]><unknown> this.orderFields,
			this.filters,
			this.index,
			this.limit,
			new OrderBy(<keyof Shipment>sortField, this.sortWay),
			true,
		)).then(res => {
			this.orders = <Order[]><unknown> res.results;
			this.filteredItems = this.typeConversion().map(x => ({ ...x }));
		}).catch((error: unknown) => {
			console.error('error requesting shipments', error);
		});
	}

	async getCountries() {
		return this.countriesService.getCountries().then(countries => {
			this.countries = countries;
			this.filteredItems = this.typeConversion().map(x => ({ ...x }));
		});
	}

	async getFastlaneItem(sortField: string | undefined) {
		return firstValueFrom(this.protoTypeService.getFastLaneItems(
			this.fastlaneItemFields,
			this.filters,
			this.index,
			this.limit,
			new OrderBy(<keyof FastLaneItems>sortField, this.sortWay),
			true
		)).then(res => {
			this.fastlaneItem = res.results;
			this.filteredItems = this.typeConversion().map(x => ({ ...x }));
		}).catch((error: unknown) => {
			console.error('error requesting orders', error);
		});
	}

	async getBatches(sortField: string | undefined) {
		return firstValueFrom(this.batchService.getDatacenterBatchesV5(
			this.batchFields,
			this.filters,
			this.index,
			this.limit,
			new OrderBy(<keyof Batch>sortField, this.sortWay),
			true
		)).then(res => {
			this.batches = res.results;
			this.filteredItems = this.typeConversion().map(x => ({ ...x }));
		}).catch((error: unknown) => {
			console.error('error requesting batches', error);
		});
	}

	async getSaOrders(sortField: string | undefined) {
		return firstValueFrom(this.scheduleBusinessOrderService.getScheduleBusinessOrders(
			this.saOrderFields,
			this.filters,
			this.index,
			this.limit,
			new OrderBy(<keyof ScheduleBusinessOrder>sortField, this.sortWay),
			true
		)).then(res => {
			this.saOrders = res.results;
			this.filteredItems = this.typeConversion().map(x => ({ ...x }));
		}).catch((error: unknown) => {
			console.error('error requesting schedule business orders', error);
		});
	}

	async getDocumentMills() {
		return firstValueFrom(this.documentService.getDocumentMills())
			.then(res => {
				this.documents = res.results;
				this.filteredItems = this.typeConversion().map(x => ({ ...x }));
			}).catch((error: unknown) => {
				console.error('error requesting documents', error);
			});
	}

	typeConversion() {
		let fields;
		const dropdownList: DropdownList[] = [];
		switch (this.datacenterType) {
		case 'order':
		case 'shipment':
			if (this.params?.colDef.colId === 'status' || this.params?.colDef.colId === 'carrierNameConcat') {
				fields = this.batchFields;
				for (const item of this.batches) {
					if (item[fields[0]] && item[fields[1]]) {
						const dropdown: DropdownList = {
							code: <string> item[fields[0]],
							translation: <string> item[fields[1]],
							fixedList: false
						};
						dropdownList.push(dropdown);
					}
				}
			} else {
				fields = this.orderFields;
				for (const item of this.orders) {
					if (item[fields[0]] && item[fields[1]]) {
						if (this.params?.filterType === 'SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER') {
							this.appendMultiValueDropdowns(
								dropdownList,
								<string>item[fields[0]],
								<string>item[fields[1]]
							);
							continue;
						}
						const dropdown: DropdownList = {
							code: <string> item[fields[0]],
							translation: <string> item[fields[1]],
							fixedList: false
						};
						dropdownList.push(dropdown);
					}
				}
			}
			break;
		case 'users':
		case 'organization':
			for (const item of this.countries) {
				if (item['countryCode'] && item['countryDescription']) {
					const dropdown: DropdownList = {
						code: item['countryCode'],
						translation: item['countryDescription'],
						fixedList: false
					};
					dropdownList.push(dropdown);
				}
			}
			for (const item of this.services) {
				if (item['service']['id'] && item['service']['labelTranslationId']) {
					const dropdown: DropdownList = {
						code: item['service']['id'],
						translation: item['service']['labelTranslationId'],
						fixedList: false
					};
					dropdownList.push(dropdown);
				}
			}
			break;
		case 'batch':
		case 'calloff-by-batch':
			fields = this.batchFields;
			for (const item of this.batches) {
				if (item[fields[0]] && item[fields[1]]) {
					const dropdown: DropdownList = {
						code: <string>item[fields[0]],
						translation: <string>item[fields[1]],
						fixedList: false
					};
					dropdownList.push(dropdown);
				}
			}
			break;
		case 'schedule-business-order':
			fields = this.saOrderFields;
			for (const item of this.saOrders) {
				if (item[fields[0]] && item[fields[1]]) {
					const dropdown: DropdownList = {
						code: <string> item[fields[0]],
						translation: <string> item[fields[1]],
						fixedList: false
					};
					dropdownList.push(dropdown);
				}
			}
			break;
		case 'document':
			fields = this.documentFields;
			for (const item of this.documents) {
				if (item[fields[0]] && item[fields[1]]) {
					const dropdown: DropdownList = {
						code: <string>item[fields[0]],
						translation: <string>item[fields[1]],
						fixedList: false
					};
					dropdownList.push(dropdown);
				}
			}
			break;
		case 'prototypes':
			fields = this.fastlaneItemFields;
			for (const item of this.fastlaneItem) {
				if (item[fields[0]]) {
					const dropdown: DropdownList = {
						code: <string>item[fields[0]],
						translation: <string>item[fields[0]],
						fixedList: false
					};
					dropdownList.push(dropdown);
				}
			}
			break;
		default:
			throw new Error('unknown datacentertype type');
		}
		return this.deduplicateDropdownList(dropdownList);
	}

	private setClientSideUserDropdownValues() {
		switch (this.params?.colDef.colId) {
		case 'role':
			this.filteredItems = staticUserDropdownValues.map(x => ({ ...x }));
			break;
		case 'status':
			this.filteredItems = staticOrganizationCardUserStatusDropdownValues.map(x => ({ ...x }));
			break;
		case 'personStatus':
			this.filteredItems = staticUserPersonStatusDropdownValues.map(x => ({ ...x }));
			break;
		default:
			break;
		}
	}

	private getClientSideUserOrganizationStatus(data: Record<string, unknown>) {
		const personMemberStatus = this.getStringValue(data, 'personMembers.status').trim().toLowerCase();
		const personStatus = this.getStringValue(data, 'status').trim().toLowerCase();
		if (personMemberStatus === 'disab' || personStatus === 'disab') {
			return 'disab';
		}
		if (personStatus === 'dact' || personMemberStatus === 'dact') {
			return 'dact';
		}
		// personMembers.status='pend' means pending admin validation in organization.
		if (personMemberStatus === 'pend') {
			return 'pend';
		}
		// personStatus='pend' means waiting for user validation after admin validation.
		if (personStatus === 'pend') {
			// Comparison uses a lower-cased selected code.
			return 'uservalidationstatus';
		}
		return 'activ';
	}

	private getStringValue(data: Record<string, unknown>, field?: string) {
		const value = this.getNestedValue(data, field);
		return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
	}

	private getNestedValue(data: Record<string, unknown>, field?: string) {
		if (!field) {
			return undefined;
		}
		return field.split('.').reduce<unknown>((currentValue, part) => {
			if (currentValue && typeof currentValue === 'object') {
				return (<Record<string, unknown>> currentValue)[part];
			}
			return undefined;
		}, data);
	}

	private appendMultiValueDropdowns(dropdownList: DropdownList[], codesRaw: string, translationsRaw: string) {
		const codes = codesRaw.split(';').map(value => value.trim());
		const translations = translationsRaw.split(';').map(value => value.trim());
		const count = Math.min(codes.length, translations.length);

		for (let index = 0; index < count; index++) {
			const code = codes[index];
			const translation = translations[index];
			if (!code || !translation) {
				continue;
			}
			dropdownList.push({
				code,
				translation,
				fixedList: false
			});
		}
	}

	private deduplicateDropdownList(dropdownList: DropdownList[]) {
		return Array.from(new Map(
			dropdownList.map(item => [`${ item.code }::${ item.translation }`, item])
		).values());
	}

	private translateAndSortValues() {
		for (const filterItem of this.filteredItems) {
			if (filterItem.fixedList) {
				this.itemsSub = this.translateService.get(filterItem.translation)
					.subscribe({
						next: value => {
							filterItem.translation = <string>value;
						}
					});
			}
		}
		this.filteredItems = [...this.filteredItems].sort(
			(f1, f2) => f1.translation.localeCompare(f2.translation)
		);
		if (this.params && !this.params.removeNoValueFilter) {
			const noValueDropdown = {
				code: 'novalue',
				translation: this.translationService.getTR('filter.no-value'),
				fixedList: false
			};
			this.filteredItems.unshift(noValueDropdown);
		}
	}
}
